import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, API } from "../lib/api";
import { toast } from "sonner";
import TopNav from "../components/TopNav";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import RichEditor from "../components/RichEditor";
import {
  Paperclip, Trash, ArrowLeft, GoogleDriveLogo,
  FileText, FilePdf, FileVideo, FileAudio, Image as ImageIcon, FloppyDisk,
} from "@phosphor-icons/react";

function attachmentIcon(ct = "") {
  if (ct.startsWith("image/")) return ImageIcon;
  if (ct.startsWith("video/")) return FileVideo;
  if (ct.startsWith("audio/")) return FileAudio;
  if (ct.includes("pdf")) return FilePdf;
  return FileText;
}

function humanBytes(b) {
  if (!b) return "0 B";
  const k = 1024, s = ["B", "KB", "MB", "GB"];
  const i = Math.min(Math.floor(Math.log(b) / Math.log(k)), s.length - 1);
  return (b / Math.pow(k, i)).toFixed(1) + " " + s[i];
}

const MOODS = ["Calm", "Happy", "Reflective", "Anxious", "Grateful", "Restless"];

export default function EntryDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [entry, setEntry] = useState(null);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [mood, setMood] = useState("");
  const [tagsInput, setTagsInput] = useState("");
  const [dirty, setDirty] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [useDrive, setUseDrive] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const me = await api.get("/auth/me");
      setUser(me.data);
      setUseDrive(!!me.data.drive_connected);
      const e = await api.get(`/entries/${id}`);
      setEntry(e.data);
      setTitle(e.data.title || "");
      setContent(e.data.content || "");
      setMood(e.data.mood || "");
      setTagsInput((e.data.tags || []).join(", "));
      setDirty(false);
    } catch {
      navigate("/dashboard", { replace: true });
    }
  };

  useEffect(() => { load(); }, [id]);

  const parseTags = (s) => s.split(",").map((x) => x.trim()).filter(Boolean);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.patch(`/entries/${id}`, {
        title, content, mood, tags: parseTags(tagsInput),
      });
      setEntry(r.data);
      setDirty(false);
      toast.success("Saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const onUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    const form = new FormData();
    form.append("file", f);
    form.append("use_drive", useDrive ? "true" : "false");
    try {
      await api.post(`/entries/${id}/upload`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await load();
      toast.success("Attached");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removeEntry = async () => {
    if (!confirm("Delete this entry and its attachments?")) return;
    await api.delete(`/entries/${id}`);
    navigate("/dashboard");
  };

  if (!entry || !user) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading…</div>;

  const d = new Date(entry.entry_date);

  return (
    <div className="min-h-screen paper-grain" data-testid="entry-detail-page">
      <TopNav user={user} onLogout={async () => { await api.post("/auth/logout"); navigate("/"); }} />

      <main className="max-w-3xl mx-auto px-6 py-12">
        <div className="flex items-center justify-between mb-10">
          <button
            data-testid="back-to-dashboard"
            onClick={() => navigate("/dashboard")}
            className="text-xs uppercase tracking-[0.2em] font-semibold text-muted-foreground hover:text-foreground flex items-center gap-2"
          >
            <ArrowLeft size={14} /> Back
          </button>
          <Button
            data-testid="save-entry-btn"
            onClick={save}
            disabled={!dirty || saving}
            size="sm"
            className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-40"
          >
            <FloppyDisk size={14} className="mr-2" /> {saving ? "Saving…" : dirty ? "Save changes" : "Saved"}
          </Button>
        </div>

        <div className="grid grid-cols-[100px_1fr] gap-8 mb-8">
          <div>
            <div className="font-serif text-6xl font-light leading-none">{d.getDate()}</div>
            <div className="text-xs uppercase tracking-[0.2em] mt-2 text-muted-foreground">
              {d.toLocaleString("en-US", { month: "short" })} · {d.getFullYear()}
            </div>
          </div>
          <div>
            <Input
              data-testid="edit-title-input"
              value={title}
              onChange={(e) => { setTitle(e.target.value); setDirty(true); }}
              placeholder="Untitled"
              className="lined-input rounded-none px-0 font-serif text-4xl focus-visible:ring-0 h-auto py-2"
            />
          </div>
        </div>

        <RichEditor
          value={content}
          onChange={(v) => { setContent(v); setDirty(true); }}
          placeholder="Continue writing…"
        />

        <div className="mt-6 flex flex-wrap gap-2" data-testid="mood-row">
          {MOODS.map((m) => (
            <button
              key={m}
              data-testid={`mood-${m.toLowerCase()}`}
              onClick={() => { setMood(m === mood ? "" : m); setDirty(true); }}
              className={`text-xs uppercase tracking-[0.2em] px-4 py-2 rounded-full border transition-colors ${
                mood === m
                  ? "bg-primary text-primary-foreground border-primary"
                  : "border-border hover:border-primary"
              }`}
            >
              {m}
            </button>
          ))}
        </div>

        <div className="mt-6">
          <Input
            data-testid="edit-tags-input"
            value={tagsInput}
            onChange={(e) => { setTagsInput(e.target.value); setDirty(true); }}
            placeholder="tags, comma, separated"
            className="lined-input rounded-none px-0 text-sm focus-visible:ring-0"
          />
        </div>

        {/* Attachments */}
        <section className="mt-16">
          <div className="flex items-center justify-between mb-6">
            <h3 className="font-serif text-2xl">Attachments</h3>
            <div className="flex items-center gap-3">
              {user.drive_connected && (
                <label
                  data-testid="use-drive-toggle"
                  className="text-xs uppercase tracking-[0.2em] flex items-center gap-2 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={useDrive}
                    onChange={(e) => setUseDrive(e.target.checked)}
                    className="accent-primary"
                  />
                  <GoogleDriveLogo size={14} weight="duotone" /> Save to Drive
                </label>
              )}
              <input
                ref={fileRef}
                type="file"
                onChange={onUpload}
                className="hidden"
                data-testid="file-input"
                accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.txt"
              />
              <Button
                data-testid="attach-btn"
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
                size="sm"
              >
                <Paperclip size={14} className="mr-1" /> {uploading ? "Uploading…" : "Attach"}
              </Button>
            </div>
          </div>

          {entry.attachments?.length ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4" data-testid="attachments-grid">
              {entry.attachments.map((a) => {
                const Icon = attachmentIcon(a.content_type);
                const isImage = a.content_type?.startsWith("image/");
                return (
                  <a
                    key={a.file_id}
                    href={`${API}/files/${a.file_id}`}
                    target="_blank" rel="noopener noreferrer"
                    data-testid={`attachment-${a.file_id}`}
                    className="border border-border rounded-lg bg-card hover:border-primary transition-colors overflow-hidden block"
                  >
                    <div className="h-32 bg-muted flex items-center justify-center overflow-hidden">
                      {isImage ? (
                        <img src={`${API}/files/${a.file_id}`} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <Icon size={40} weight="duotone" className="text-muted-foreground" />
                      )}
                    </div>
                    <div className="p-3">
                      <p className="text-xs truncate font-medium">{a.original_filename}</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {humanBytes(a.size)} · {a.storage === "drive" ? "Drive" : "Local"}
                      </p>
                    </div>
                  </a>
                );
              })}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">No files yet. Add photos, videos, audio or documents.</p>
          )}
        </section>

        <div className="mt-16 pt-8 border-t border-border">
          <Button
            data-testid="delete-entry-btn"
            variant="ghost"
            onClick={removeEntry}
            className="text-destructive hover:text-destructive text-xs uppercase tracking-[0.2em]"
          >
            <Trash size={14} className="mr-2" /> Delete entry
          </Button>
        </div>
      </main>
    </div>
  );
}
